import React from 'react';
import i18n from './i18n/configs';
import { useSelector } from 'react-redux';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { Provider as ReduxProvider } from 'react-redux';
import { store } from './redux/core/store';
import { Router } from './router/Router';
import { css, ThemeProvider } from '@emotion/react';
import { ThemeProvider as MUIThemeProvider } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AuthProvider } from './contexts/AuthContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { LoadingProvider } from './contexts/LoadingContext';
import dayjs from 'dayjs';
import ja from 'dayjs/locale/ja';
import 'dayjs/locale/en';
import { showMedicalContentSelector } from './redux/config/selfData';
import { createAppTheme } from './styles/theme';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGE_SENDER_ID,
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);

const ThemeVars = (theme: Theme) => css`
  --surface-global-header: ${theme.custom.surface.globalHeaderBg};
  --surface-content: ${theme.custom.surface.contentBg};
  --surface-left-nav: ${theme.custom.surface.leftNavBg};
  --surface-list-header: ${theme.custom.surface.listHeaderBg};
  --surface-card-header: ${theme.custom.surface.cardHeaderBg};
  --nav-active-bg: ${theme.custom.nav.activeBg};
  --nav-active-text: ${theme.custom.nav.activeText};
  --button-bg: ${theme.custom.button.updateBg};
  --border-card: ${theme.custom.border.card};
`;

const AppWithTheme = React.memo(() => {
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const theme = React.useMemo(() => createAppTheme(showMedicalContent), [showMedicalContent]);
  const { i18n } = useTranslation();

  return (
    <AuthProvider>
      <MUIThemeProvider theme={theme}>
        <ThemeProvider theme={theme}>
          <div css={ThemeVars(theme)}>
            <LoadingProvider>
              <SnackbarProvider>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={i18n.language.startsWith('ja') ? ja : undefined}
                >
                  <Router />
                </LocalizationProvider>
              </SnackbarProvider>
            </LoadingProvider>
          </div>
        </ThemeProvider>
      </MUIThemeProvider>
    </AuthProvider>
  );
});

function App() {
  const { i18n } = useTranslation();
  return (
    <I18nextProvider i18n={i18n}>
      <ReduxProvider store={store}>
        <AppWithTheme />
      </ReduxProvider>
    </I18nextProvider>
  );
}

export default App;
